import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

// Filter state interface - make properties required to avoid undefined errors
export interface FilterState {
  lineOfBusiness: string[];
  enabled: 'all' | 'enabled' | 'disabled';
  accountName: string;
  activeStatus: 'active' | 'inactive';
  activeFilters: string[];
}

// Helper to parse URL params into filter state
const parseUrlParams = (searchParams: URLSearchParams): Omit<FilterState, 'activeFilters'> => {
  const lob = searchParams.getAll('lob');
  const enabled = searchParams.get('enabled') as 'all' | 'enabled' | 'disabled' | null;
  const accountName = searchParams.get('search') || '';
  const activeParam = searchParams.get('active');

  return {
    lineOfBusiness: lob,
    enabled: enabled || 'all',
    accountName,
    activeStatus: activeParam === 'inactive' ? 'inactive' : 'active',
  };
};

// Helper to build active filters array from state
const buildActiveFilters = (state: Omit<FilterState, 'activeFilters'>): string[] => {
  const activeFilters: string[] = [];

  if (state.lineOfBusiness.length > 0) {
    state.lineOfBusiness.forEach(lob => {
      activeFilters.push(`Line Of Business: ${lob}`);
    });
  }

  if (state.enabled !== 'all') {
    activeFilters.push(`Strategy Portal Enabled: ${state.enabled}`);
  }

  if (state.accountName.trim() !== '') {
    activeFilters.push(`Account Name: ${state.accountName}`);
  }

  if (state.activeStatus === 'inactive') {
    activeFilters.push('Account Status: Inactive');
  }

  return activeFilters;
};

// Custom hook for filter management
export const useAccountsFilter = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const initialState = parseUrlParams(searchParams);
  const initialActiveFilters = buildActiveFilters(initialState);

  const [filterDrawerOpen, setFilterDrawerOpen] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<FilterState>({
    ...initialState,
    activeFilters: initialActiveFilters
  });

  const [tempState, setTempState] = useState<Omit<FilterState, 'activeFilters'>>(initialState);

  const [expandedAccordions, setExpandedAccordions] = useState<{[key: string]: boolean}>({
    lineOfBusiness: true,
    strategyPortal: true,
    accountName: true,
    accountStatus: true,
  });

  // Sync URL params when filter state changes
  const updateUrlParams = useCallback((state: FilterState) => {
    const params = new URLSearchParams();

    // Add line of business params
    state.lineOfBusiness.forEach(lob => {
      params.append('lob', lob);
    });

    // Add enabled param if not 'all'
    if (state.enabled !== 'all') {
      params.set('enabled', state.enabled);
    }

    // Add search/account name param
    if (state.accountName.trim() !== '') {
      params.set('search', state.accountName.trim());
    }

    // Add active status param when inactive
    if (state.activeStatus === 'inactive') {
      params.set('active', 'inactive');
    }

    setSearchParams(params, { replace: true });
  }, [setSearchParams]);

  const handleLineOfBusinessChange = (lob: string, checked: boolean) => {
    if (checked) {
      setTempState(prev => ({
        ...prev,
        lineOfBusiness: [...prev.lineOfBusiness, lob]
      }));
    } else {
      setTempState(prev => ({
        ...prev,
        lineOfBusiness: prev.lineOfBusiness.filter(item => item !== lob)
      }));
    }
  };

  const handleEnabledChange = (value: 'all' | 'enabled' | 'disabled') => {
    setTempState(prev => ({ ...prev, enabled: value }));
  };

  const handleActiveStatusChange = (value: 'active' | 'inactive') => {
    setTempState(prev => ({ ...prev, activeStatus: value }));
  };

  // Simplified account name filter handler without debounce
  const handleAccountNameFilterChange = (value: string) => {
    setTempState(prev => ({ ...prev, accountName: value }));
  };

  const applyFilters = () => {
    const newActiveFilters = buildActiveFilters(tempState);

    const newFilterState: FilterState = {
      lineOfBusiness: [...tempState.lineOfBusiness],
      enabled: tempState.enabled,
      accountName: tempState.accountName.trim(),
      activeStatus: tempState.activeStatus,
      activeFilters: newActiveFilters
    };

    setFilterState(newFilterState);
    updateUrlParams(newFilterState);
    setFilterDrawerOpen(false);
  };

  const clearFilters = () => {
    const emptyState: FilterState = {
      lineOfBusiness: [],
      enabled: 'all',
      accountName: '',
      activeStatus: 'active',
      activeFilters: [],
    };

    setFilterState(emptyState);
    setTempState({
      lineOfBusiness: [],
      enabled: 'all',
      accountName: '',
      activeStatus: 'active',
    });
    updateUrlParams(emptyState);
  };

  const removeFilter = (filter: string) => {
    const newFilterState = { ...filterState };

    if (filter.startsWith('Line Of Business:')) {
      const lob = filter.split(':')[1].trim();
      newFilterState.lineOfBusiness = newFilterState.lineOfBusiness.filter(item => item !== lob);
      setTempState(prev => ({
        ...prev,
        lineOfBusiness: prev.lineOfBusiness.filter(item => item !== lob)
      }));
    } else if (filter.startsWith('Strategy Portal Enabled:')) {
      newFilterState.enabled = 'all';
      setTempState(prev => ({ ...prev, enabled: 'all' }));
    } else if (filter.startsWith('Account Name:')) {
      newFilterState.accountName = '';
      setTempState(prev => ({ ...prev, accountName: '' }));
    } else if (filter.startsWith('Account Status:')) {
      newFilterState.activeStatus = 'active';
      setTempState(prev => ({ ...prev, activeStatus: 'active' }));
    }

    newFilterState.activeFilters = newFilterState.activeFilters.filter(item => item !== filter);
    setFilterState(newFilterState);
    updateUrlParams(newFilterState);
  };

  useEffect(() => {
    setTempState({
      lineOfBusiness: [...filterState.lineOfBusiness],
      enabled: filterState.enabled,
      accountName: filterState.accountName,
      activeStatus: filterState.activeStatus,
    });
  }, [filterState]);

  return {
    filterState,
    tempState,
    filterDrawerOpen,
    expandedAccordions,
    setFilterDrawerOpen,
    setExpandedAccordions,
    handleLineOfBusinessChange,
    handleEnabledChange,
    handleActiveStatusChange,
    handleAccountNameFilterChange,
    applyFilters,
    clearFilters,
    removeFilter,
  };
};

export default useAccountsFilter;
